import api from './api';

export interface ScheduleException {
  id: number;
  title: string;
  description: string;
  exception_type: 'unavailable' | 'collective' | 'special';
  start_date: string;
  end_date: string;
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UnavailableDate {
  date: string;
  title: string;
  description: string;
  is_all_day: boolean;
  start_time?: string;
  end_time?: string;
}

class ScheduleExceptionApiService {
  /**
   * Récupère toutes les exceptions de planning actives
   */
  async getScheduleExceptions(): Promise<ScheduleException[]> {
    try {
      console.log('🔄 Récupération des exceptions de planning...');
      const response = await api.get('/api/schedule-exceptions/');
      console.log('✅ Exceptions récupérées:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des exceptions:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des exceptions');
    }
  }

  /**
   * Récupère uniquement les dates indisponibles pour le calendrier
   */
  async getUnavailableDates(): Promise<UnavailableDate[]> {
    try {
      console.log('🔄 Récupération des dates indisponibles...');
      const response = await api.get('/api/unavailable-dates/');
      console.log('✅ Dates indisponibles récupérées:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Erreur lors de la récupération des dates indisponibles:', error);
      throw new Error(error.response?.data?.error || 'Erreur lors de la récupération des dates indisponibles');
    }
  }

  /**
   * Vérifie si une date est indisponible
   */
  isDateUnavailable(date: string, unavailableDates: UnavailableDate[]): boolean {
    return unavailableDates.some(unavailable => unavailable.date === date);
  }

  /**
   * Récupère les informations d'indisponibilité pour une date
   */
  getUnavailabilityInfo(date: string, unavailableDates: UnavailableDate[]): UnavailableDate | null {
    return unavailableDates.find(unavailable => unavailable.date === date) || null;
  }

  /**
   * Vérifie si un créneau horaire est affecté par une indisponibilité partielle
   */
  isTimeSlotAffected(date: string, timeSlot: string, unavailableDates: UnavailableDate[]): boolean {
    const unavailability = this.getUnavailabilityInfo(date, unavailableDates);
    
    if (!unavailability) return false;
    if (unavailability.is_all_day) return true;
    
    // Si ce n'est pas toute la journée, vérifier les heures
    if (unavailability.start_time && unavailability.end_time) {
      const slotTime = timeSlot + ':00'; // Ajouter les secondes pour la comparaison
      return slotTime >= unavailability.start_time && slotTime <= unavailability.end_time;
    }
    
    return false;
  }

  /**
   * Formate une date pour l'affichage
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Génère un message d'indisponibilité pour une date
   */
  getUnavailabilityMessage(date: string, unavailableDates: UnavailableDate[]): string {
    const unavailability = this.getUnavailabilityInfo(date, unavailableDates);
    
    if (!unavailability) return '';
    
    if (unavailability.is_all_day) {
      return `${unavailability.title} - Indisponible toute la journée`;
    } else if (unavailability.start_time && unavailability.end_time) {
      return `${unavailability.title} - Indisponible de ${unavailability.start_time} à ${unavailability.end_time}`;
    } else {
      return unavailability.title;
    }
  }

  /**
   * Groupe les exceptions par type
   */
  groupExceptionsByType(exceptions: ScheduleException[]): Record<string, ScheduleException[]> {
    return exceptions.reduce((groups, exception) => {
      if (!groups[exception.exception_type]) {
        groups[exception.exception_type] = [];
      }
      groups[exception.exception_type].push(exception);
      return groups;
    }, {} as Record<string, ScheduleException[]>);
  }

  /**
   * Filtre les exceptions par période
   */
  filterExceptionsByPeriod(
    exceptions: ScheduleException[], 
    startDate: string, 
    endDate: string
  ): ScheduleException[] {
    return exceptions.filter(exception => {
      // Une exception est dans la période si elle chevauche avec la période demandée
      return exception.start_date <= endDate && exception.end_date >= startDate;
    });
  }
}

export const scheduleExceptionApiService = new ScheduleExceptionApiService();
